import { ScanResult, ScanSummary, createScanSummary } from './types';

// ============= WCAG Color Contrast Utilities =============

/**
 * Parse RGB values from CSS color string
 */
function parseRGB(color: string): { r: number; g: number; b: number } | null {
  // Handle rgb(r, g, b) format
  const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (rgbMatch) {
    return {
      r: parseInt(rgbMatch[1]),
      g: parseInt(rgbMatch[2]),
      b: parseInt(rgbMatch[3])
    };
  }
  
  // Handle rgba(r, g, b, a) format
  const rgbaMatch = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
  if (rgbaMatch) {
    return {
      r: parseInt(rgbaMatch[1]),
      g: parseInt(rgbaMatch[2]),
      b: parseInt(rgbaMatch[3])
    };
  }
  
  return null;
}

/**
 * Calculate relative luminance per WCAG 2.1
 * https://www.w3.org/WAI/WCAG21/Understanding/contrast-minimum.html
 */
function getLuminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r, g, b].map(c => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Calculate contrast ratio between two colors (WCAG formula)
 */
function getContrastRatio(l1: number, l2: number): number {
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Check if font size qualifies as "large text" for WCAG
 * Large text: 18pt (24px) or 14pt (18.66px) bold
 */
function isLargeText(element: Element): boolean {
  const styles = window.getComputedStyle(element);
  const fontSize = parseFloat(styles.fontSize);
  const fontWeight = parseInt(styles.fontWeight) || 400;
  
  if (fontSize >= 24) return true;
  if (fontSize >= 18.66 && fontWeight >= 700) return true;
  return false;
}

/**
 * Get effective background color (traversing up the DOM if transparent)
 */
function getEffectiveBackgroundColor(element: Element): string {
  let current: Element | null = element;
  
  while (current) {
    const bg = window.getComputedStyle(current).backgroundColor;
    const parsed = parseRGB(bg);
    
    // If we have a non-transparent background, return it
    if (parsed && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') {
      // Check alpha channel for rgba
      const alphaMatch = bg.match(/rgba\([\d,\s]+,\s*([\d.]+)\)/);
      if (!alphaMatch || parseFloat(alphaMatch[1]) > 0.1) {
        return bg;
      }
    }
    
    current = current.parentElement;
  }
  
  // Default to white if no background found
  return 'rgb(255, 255, 255)';
}

// ============= Main Accessibility Scan =============

export function runAccessibilityScan(): ScanSummary {
  console.group('♿ Accessibility (A11y) Scan');
  
  const results: ScanResult[] = [];

  try {
    // ========== Check 1: Button Accessibility ==========
    const buttons = document.querySelectorAll('button');
    const buttonsWithoutLabel: string[] = [];

    buttons.forEach((button, index) => {
      const isThirdPartyInternal =
        button.closest('[data-radix-scroll-area-viewport]') ||
        button.closest('[data-radix-scroll-area-thumb]') ||
        button.closest('[data-radix-select-viewport]') ||
        button.closest('[data-radix-select-content]') ||
        button.closest('.rdp') ||
        button.closest('[data-debug-ignore-a11y]');

      if (isThirdPartyInternal) return;

      const hasAriaLabel = button.hasAttribute('aria-label') || button.hasAttribute('aria-labelledby');
      const hasTextContent = (button.textContent?.trim().length || 0) > 0;

      if (!hasAriaLabel && !hasTextContent) {
        const descriptor = [
          `Button ${index + 1}`,
          `class="${button.className.slice(0, 50)}"`,
          button.id ? `id="${button.id}"` : '',
        ].filter(Boolean).join(' ');
        buttonsWithoutLabel.push(descriptor);
      }
    });

    results.push({
      name: 'Button Accessibility',
      status: buttonsWithoutLabel.length === 0 ? 'pass' : 'fail',
      message: buttonsWithoutLabel.length === 0
        ? `All ${buttons.length} buttons have labels`
        : `${buttonsWithoutLabel.length} button(s) missing labels`,
      details: buttonsWithoutLabel.length > 0 ? buttonsWithoutLabel.join('; ') : 'All buttons accessible'
    });

    // ========== Check 2: Image Alt Text ==========
    const images = document.querySelectorAll('img');
    const imagesWithoutAlt: string[] = [];

    images.forEach((img, index) => {
      if (!img.hasAttribute('alt')) {
        const src = img.src.split('/').pop()?.slice(0, 30) || 'unknown';
        imagesWithoutAlt.push(`Image ${index + 1}: ${src}`);
      }
    });

    results.push({
      name: 'Image Alt Text',
      status: imagesWithoutAlt.length === 0 ? 'pass' : 'fail',
      message: imagesWithoutAlt.length === 0
        ? `All ${images.length} images have alt text`
        : `${imagesWithoutAlt.length} image(s) missing alt text`,
      details: imagesWithoutAlt.length > 0 ? imagesWithoutAlt.join('; ') : 'All images accessible'
    });

    // ========== Check 3: Form Input Labels ==========
    const inputs = document.querySelectorAll('input, textarea, select');
    const inputsWithoutLabel: string[] = [];

    inputs.forEach((input, index) => {
      // Skip hidden inputs
      if (input.getAttribute('type') === 'hidden') return;
      
      const hasLabel = input.hasAttribute('aria-label') || 
                      input.hasAttribute('aria-labelledby') ||
                      input.hasAttribute('placeholder') ||
                      document.querySelector(`label[for="${input.id}"]`);

      if (!hasLabel) {
        inputsWithoutLabel.push(`Input ${index + 1}: ${input.getAttribute('name') || input.getAttribute('type') || 'unknown'}`);
      }
    });

    results.push({
      name: 'Form Input Labels',
      status: inputsWithoutLabel.length === 0 ? 'pass' : 'fail',
      message: inputsWithoutLabel.length === 0
        ? `All ${inputs.length} input(s) have labels`
        : `${inputsWithoutLabel.length} input(s) missing labels`,
      details: inputsWithoutLabel.length > 0 ? inputsWithoutLabel.join('; ') : 'All inputs accessible'
    });

    // ========== Check 4: Touch Target Size (44x44px minimum) ==========
    const interactiveElements = document.querySelectorAll('button, a, input, select, textarea');
    const smallTargets: string[] = [];

    interactiveElements.forEach((element, index) => {
      // Skip keyboard keys and very small decorative elements
      if (element.closest('[class*="keyboard"]') || 
          element.closest('[data-keyboard-key]') ||
          element.closest('[data-debug-ignore-a11y]')) {
        return;
      }
      
      const rect = element.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0 && (rect.width < 44 || rect.height < 44)) {
        const tagName = element.tagName.toLowerCase();
        smallTargets.push(`${tagName} ${index + 1}: ${rect.width.toFixed(0)}x${rect.height.toFixed(0)}px`);
      }
    });

    results.push({
      name: 'Touch Target Size',
      status: smallTargets.length === 0 ? 'pass' : 'info',
      message: smallTargets.length === 0
        ? 'All interactive elements meet minimum size (44x44px)'
        : `${smallTargets.length} element(s) below minimum size`,
      details: smallTargets.length > 0 ? smallTargets.slice(0, 5).join('; ') : 'All targets accessible'
    });

    // ========== Check 5: WCAG Color Contrast (AA Standard) ==========
    const textElements = document.querySelectorAll('p, h1, h2, h3, h4, h5, h6, span, a, button, label, li');
    const contrastIssues: { element: string; ratio: number; required: number }[] = [];
    let checkedCount = 0;

    textElements.forEach((element, index) => {
      // Skip invisible or empty elements
      const rect = element.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      if (!element.textContent?.trim()) return;
      
      // Skip decorative elements with aria-hidden
      if (element.getAttribute('aria-hidden') === 'true') return;
      if (element.closest('[aria-hidden="true"]')) return;
      
      // Skip debug panel elements
      if (element.closest('[data-debug-panel]')) return;
      if (element.closest('[data-debug-ignore-a11y]')) return;
      
      // Skip purely decorative bullet separators
      const text = element.textContent?.trim();
      if (text === '•' || text === '·' || text === '|') return;
      
      // Skip keyboard keys and keyboard container (game-state styling)
      if (element.hasAttribute('data-keyboard-key')) return;
      if (element.closest('[data-keyboard-key]')) return;
      if (element.closest('[role="group"][aria-label="On-screen keyboard"]')) return;
      if (element.closest('[data-game-keyboard-container]')) return;
      
      // Skip game tiles and game grid (game-state styling, intentional visual feedback)
      if (element.hasAttribute('data-game-tile')) return;
      if (element.closest('[data-game-tile]')) return;
      if (element.closest('[data-game-grid]')) return;
      if (element.closest('[data-game-area]')) return;
      if (element.closest('[role="grid"]')) return;
      
      const styles = window.getComputedStyle(element);
      const textColor = styles.color;
      const bgColor = getEffectiveBackgroundColor(element);
      
      const textRGB = parseRGB(textColor);
      const bgRGB = parseRGB(bgColor);
      
      if (!textRGB || !bgRGB) return;
      
      checkedCount++;
      
      const textLuminance = getLuminance(textRGB.r, textRGB.g, textRGB.b);
      const bgLuminance = getLuminance(bgRGB.r, bgRGB.g, bgRGB.b);
      const ratio = getContrastRatio(textLuminance, bgLuminance);
      
      // WCAG AA: 4.5:1 for normal text, 3:1 for large text
      const requiredRatio = isLargeText(element) ? 3 : 4.5;
      
      if (ratio < requiredRatio) {
        const tagName = element.tagName.toLowerCase();
        contrastIssues.push({
          element: `${tagName} ${index + 1}`,
          ratio: Math.round(ratio * 100) / 100,
          required: requiredRatio
        });
      }
    });

    // Be lenient: treat 1-2 minor contrast issues as info, 3-5 as warning, >5 as fail
    // Many issues are decorative or non-essential text that doesn't impact usability
    results.push({
      name: 'Color Contrast (WCAG AA)',
      status: contrastIssues.length === 0 ? 'pass' : 
              contrastIssues.length <= 2 ? 'info' :
              contrastIssues.length <= 5 ? 'warn' : 'fail',
      message: contrastIssues.length === 0
        ? `${checkedCount} elements checked - all pass WCAG AA`
        : `${contrastIssues.length} element(s) with low contrast (review recommended)`,
      details: contrastIssues.length > 0 
        ? contrastIssues.slice(0, 3).map(i => `${i.element}: ${i.ratio}:1 (need ${i.required}:1)`).join('; ')
        : `All ${checkedCount} text elements meet WCAG AA contrast (4.5:1 normal, 3:1 large)`
    });

    // ========== Check 6: Heading Hierarchy ==========
    const headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
    const headingLevels: number[] = [];
    const hierarchyIssues: string[] = [];
    
    headings.forEach(heading => {
      const level = parseInt(heading.tagName[1]);
      headingLevels.push(level);
    });
    
    // Check for h1 presence
    const h1Count = headingLevels.filter(l => l === 1).length;
    if (h1Count === 0) {
      hierarchyIssues.push('No H1 heading found');
    } else if (h1Count > 1) {
      hierarchyIssues.push(`Multiple H1 headings (${h1Count}) - should have only one`);
    }
    
    // Check for skipped levels
    for (let i = 1; i < headingLevels.length; i++) {
      if (headingLevels[i] > headingLevels[i - 1] + 1) {
        hierarchyIssues.push(`Skipped heading level: H${headingLevels[i - 1]} → H${headingLevels[i]}`);
      }
    }

    results.push({
      name: 'Heading Hierarchy',
      status: hierarchyIssues.length === 0 ? 'pass' : 'warn',
      message: hierarchyIssues.length === 0
        ? `${headings.length} headings with proper hierarchy`
        : `${hierarchyIssues.length} heading issue(s)`,
      details: hierarchyIssues.length > 0 ? hierarchyIssues.join('; ') : 'Heading structure is valid'
    });

    // ========== Check 7: ARIA Role Validation ==========
    const ariaElements = document.querySelectorAll('[role]');
    const ariaIssues: string[] = [];
    
    const validRoles = [
      'alert', 'alertdialog', 'application', 'article', 'banner', 'button', 'cell', 
      'checkbox', 'columnheader', 'combobox', 'complementary', 'contentinfo', 'definition',
      'dialog', 'directory', 'document', 'feed', 'figure', 'form', 'grid', 'gridcell',
      'group', 'heading', 'img', 'link', 'list', 'listbox', 'listitem', 'log', 'main',
      'marquee', 'math', 'menu', 'menubar', 'menuitem', 'menuitemcheckbox', 'menuitemradio',
      'navigation', 'none', 'note', 'option', 'presentation', 'progressbar', 'radio',
      'radiogroup', 'region', 'row', 'rowgroup', 'rowheader', 'scrollbar', 'search',
      'searchbox', 'separator', 'slider', 'spinbutton', 'status', 'switch', 'tab',
      'table', 'tablist', 'tabpanel', 'term', 'textbox', 'timer', 'toolbar', 'tooltip', 'tree',
      'treegrid', 'treeitem'
    ];
    
    ariaElements.forEach((element, index) => {
      const role = element.getAttribute('role');
      if (role && !validRoles.includes(role)) {
        ariaIssues.push(`Invalid role "${role}" on element ${index + 1}`);
      }
      
      // Check for redundant ARIA (role="button" on <button>)
      const tagName = element.tagName.toLowerCase();
      if (tagName === 'button' && role === 'button') {
        ariaIssues.push(`Redundant role="button" on <button>`);
      }
      if (tagName === 'a' && role === 'link') {
        ariaIssues.push(`Redundant role="link" on <a>`);
      }
    });

    results.push({
      name: 'ARIA Role Validation',
      status: ariaIssues.length === 0 ? 'pass' : 'warn',
      message: ariaIssues.length === 0
        ? `${ariaElements.length} ARIA role(s) validated`
        : `${ariaIssues.length} ARIA issue(s) found`,
      details: ariaIssues.length > 0 ? ariaIssues.slice(0, 3).join('; ') : 'All ARIA roles are valid'
    });

    // ========== Check 8: Link Accessibility ==========
    const links = document.querySelectorAll('a');
    const linkIssues: string[] = [];
    const genericLinkTexts = ['click here', 'read more', 'learn more', 'here', 'link', 'more'];
    
    links.forEach((link, index) => {
      const text = link.textContent?.trim().toLowerCase() || '';
      const ariaLabel = link.getAttribute('aria-label')?.toLowerCase() || '';
      const href = link.getAttribute('href');
      
      // Empty links
      if (!text && !ariaLabel && !link.querySelector('img[alt]')) {
        linkIssues.push(`Link ${index + 1}: Empty link (no text or aria-label)`);
      }
      
      // Generic link text
      if (genericLinkTexts.includes(text) && !ariaLabel) {
        linkIssues.push(`Link ${index + 1}: Generic text "${text}"`);
      }
      
      // External links without warning
      const isExternal = href?.startsWith('http') && !href?.includes(window.location.hostname);
      const hasNewWindow = link.getAttribute('target') === '_blank';
      if (isExternal && hasNewWindow && !ariaLabel?.includes('new')) {
        // Just log, not a hard fail - many sites skip this
      }
    });

    results.push({
      name: 'Link Accessibility',
      status: linkIssues.length === 0 ? 'pass' : 'warn',
      message: linkIssues.length === 0
        ? `${links.length} link(s) accessible`
        : `${linkIssues.length} link issue(s)`,
      details: linkIssues.length > 0 ? linkIssues.slice(0, 3).join('; ') : 'All links have descriptive text'
    });

    // ========== Check 9: Keyboard Navigation ==========
    const tabindexElements = document.querySelectorAll('[tabindex]');
    const keyboardIssues: string[] = [];
    
    tabindexElements.forEach((element, index) => {
      const tabindex = parseInt(element.getAttribute('tabindex') || '0');
      if (tabindex > 0) {
        keyboardIssues.push(`Element ${index + 1}: positive tabindex="${tabindex}" (anti-pattern)`);
      }
    });

    results.push({
      name: 'Keyboard Navigation',
      status: keyboardIssues.length === 0 ? 'pass' : 'warn',
      message: keyboardIssues.length === 0
        ? 'No keyboard navigation issues detected'
        : `${keyboardIssues.length} keyboard issue(s)`,
      details: keyboardIssues.length > 0 
        ? keyboardIssues.slice(0, 3).join('; ') 
        : 'Tab order follows DOM order (no positive tabindex values)'
    });

    // ========== Check 10: Animation & Motion ==========
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const animatedElements = document.querySelectorAll('[class*="animate"], [class*="transition"]');
    
    results.push({
      name: 'Motion Preferences',
      status: 'info',
      message: prefersReducedMotion 
        ? 'User prefers reduced motion - verify animations respect this'
        : `${animatedElements.length} animated element(s) detected`,
      details: prefersReducedMotion 
        ? 'Check that animations are disabled or reduced for this user' 
        : 'Test with prefers-reduced-motion: reduce to verify compliance'
    });

    // ========== Check 11: Landmark Regions ==========
    const mainLandmarks = document.querySelectorAll('main, [role="main"]');
    const navLandmarks = document.querySelectorAll('nav, [role="navigation"]');
    const landmarkIssues: string[] = [];
    
    if (mainLandmarks.length === 0) {
      landmarkIssues.push('No main landmark found');
    } else if (mainLandmarks.length > 1) {
      landmarkIssues.push(`Multiple main landmarks (${mainLandmarks.length})`);
    }
    
    // Check if multiple nav elements have labels
    if (navLandmarks.length > 1) {
      let unlabeledNavs = 0;
      navLandmarks.forEach(nav => {
        if (!nav.hasAttribute('aria-label') && !nav.hasAttribute('aria-labelledby')) {
          unlabeledNavs++;
        }
      });
      if (unlabeledNavs > 1) {
        landmarkIssues.push(`${unlabeledNavs} navigation landmarks without unique labels`);
      }
    }

    const allLandmarks = document.querySelectorAll('main, header, footer, nav, aside, [role="banner"], [role="contentinfo"]');
    
    results.push({
      name: 'Landmark Regions',
      status: landmarkIssues.length === 0 ? 'pass' : 'warn',
      message: landmarkIssues.length === 0
        ? `${allLandmarks.length} landmark region(s) properly configured`
        : `${landmarkIssues.length} landmark issue(s)`,
      details: landmarkIssues.length > 0 ? landmarkIssues.join('; ') : 'Landmarks help screen reader navigation'
    });

    // ========== Check 12: Language Attribute ==========
    const htmlLang = document.documentElement.getAttribute('lang');
    results.push({
      name: 'Language Attribute',
      status: htmlLang ? 'pass' : 'warn',
      message: htmlLang ? `Language set to: ${htmlLang}` : 'HTML lang attribute missing',
      details: 'Helps screen readers pronounce content correctly'
    });

    // ========== Check 13: Page Title ==========
    const pageTitle = document.title;
    results.push({
      name: 'Page Title',
      status: pageTitle && pageTitle.length > 0 ? 'pass' : 'fail',
      message: pageTitle ? `Title: "${pageTitle.slice(0, 50)}${pageTitle.length > 50 ? '...' : ''}"` : 'Page title missing',
      details: 'Critical for screen reader users and SEO'
    });

    // ========== Check 14: Skip Links ==========
    const skipLinks = document.querySelectorAll('a[href^="#main"], a[href^="#content"], .skip-link, [class*="skip"]');
    results.push({
      name: 'Skip Navigation Links',
      status: skipLinks.length > 0 ? 'pass' : 'info',
      message: skipLinks.length > 0 ? `${skipLinks.length} skip link(s) detected` : 'No skip links found',
      details: 'Skip links help keyboard users bypass navigation'
    });

    // ========== Check 15: Semantic HTML ==========
    const semanticElements = document.querySelectorAll('main, header, footer, nav, article, section, aside');
    results.push({
      name: 'Semantic HTML',
      status: semanticElements.length > 0 ? 'pass' : 'warn',
      message: `${semanticElements.length} semantic element(s) found`,
      details: 'Semantic HTML improves screen reader navigation'
    });

    // ========== Check 16: Focus Visibility (Sampling) ==========
    const focusableForTest = document.querySelectorAll('button:not([disabled]), a[href]');
    let focusIssueCount = 0;
    const testedCount = Math.min(focusableForTest.length, 5); // Sample first 5
    
    for (let i = 0; i < testedCount; i++) {
      const element = focusableForTest[i] as HTMLElement;
      const originalOutline = window.getComputedStyle(element).outline;
      
      // Check if outline is explicitly removed without replacement
      if (originalOutline === 'none' || originalOutline === '0px none') {
        const boxShadow = window.getComputedStyle(element).boxShadow;
        const border = window.getComputedStyle(element).border;
        // If no outline and no shadow/border that could serve as focus indicator
        if (boxShadow === 'none' && !border.includes('solid')) {
          focusIssueCount++;
        }
      }
    }

    results.push({
      name: 'Focus Visibility',
      status: focusIssueCount === 0 ? 'pass' : 'warn',
      message: focusIssueCount === 0
        ? `Sampled ${testedCount} elements - focus styles present`
        : `${focusIssueCount} element(s) may lack visible focus indicators`,
      details: 'Verify focus indicators via keyboard Tab navigation'
    });

  } catch (error) {
    results.push({
      name: 'Accessibility Scan Error',
      status: 'fail',
      message: `Failed to run accessibility scan: ${error}`,
      details: String(error)
    });
  }

  const summary = createScanSummary('Accessibility (A11y) Scan', results);
  
  console.log(`✅ Passed: ${summary.passCount}`);
  console.log(`⚠️  Warnings: ${summary.warnCount}`);
  console.log(`❌ Failed: ${summary.failCount}`);
  console.log(`ℹ️  Info: ${summary.infoCount}`);
  console.table(results);
  console.groupEnd();
  
  return summary;
}
